import { all, put, takeEvery, call } from 'redux-saga/effects';
import { IRequestParams, request } from 'helpers/request';
import { logSagaErrorMessage, CONSTANTS } from 'utils';
import { AxiosResponse } from 'axios';
import { uploadFileToDB, requestSagaFactory } from 'helpers';
import { ISurveyCardList } from 'modules/admin-panel/models';
import {
	createUserPassword,
	getLastCreatedSurveyList,
	getUserDetails,
	postUserPhoto,
	updateUserDetails,
	resetPasswordByAdmin,
	deleteUserByAdmin,
	deleteUserByThemselves,
} from '../settings.actions';
import * as types from '../ActionTypes';
import { ISettingsPayload, IDeleteUserPayload } from '../models';
import { PersonalInfoModalValues } from '../components';
import { getClientUsersSaga } from '../../clients/clients.saga';
import { getPartnerAdminsSaga } from '../../partners/partners.saga';
import { updateUserName } from '../../login/auth.actions';

const ENDPOINTS = {
	postUserPhoto: '/users/profile-picture',
};

function* getLastSentSurveyListSaga() {
	try {
		yield put(getLastCreatedSurveyList.loading());
		const lastSentSurveyListParams: IRequestParams = {
			url: `${CONSTANTS.baseApiPath}${CONSTANTS.moduleAdminPath}/surveys/lastCreated`,
		};

		const { data }: AxiosResponse<ISurveyCardList> = yield call(
			request,
			lastSentSurveyListParams,
		);

		yield put(getLastCreatedSurveyList.success(data));
	} catch (e) {
		logSagaErrorMessage('getLastSentSurveyList', e);
		yield put(getLastCreatedSurveyList.fail(e));
	}
}

export function* getUserDetailsSaga() {
	try {
		yield put(getUserDetails.loading());
		const userDetailsParams: IRequestParams = {
			url: `${CONSTANTS.baseApiPath}${CONSTANTS.moduleAdminPath}/users/current/details`,
		};

		const { data }: AxiosResponse<ISettingsPayload> = yield call(
			request,
			userDetailsParams,
		);

		yield put(getUserDetails.success(data));
	} catch (e) {
		logSagaErrorMessage('getUserDetails', e);
		yield put(getUserDetails.fail(e));
	}
}

function* resetPasswordByAdminSaga({
	payload,
}: {
	payload: {
		newPassword: string;
		login: string;
	};
}) {
	try {
		yield put(resetPasswordByAdmin.loading());

		const userDetailsParams: IRequestParams = {
			method: 'POST',
			data: payload,
			url: `${CONSTANTS.baseApiPath}${CONSTANTS.moduleAdminPath}/users/change-password-by-admin`,
		};

		yield call(request, userDetailsParams);

		yield put(resetPasswordByAdmin.success('data'));
	} catch (e) {
		logSagaErrorMessage('resetUserPasswordByAdmin', e);
		yield put(resetPasswordByAdmin.fail(e));
	}
}

function* deleteUserByAdminSaga({ payload }: { payload: IDeleteUserPayload }) {
	try {
		yield put(deleteUserByAdmin.loading());

		const { login, email, role, companyType, companyId } = payload;

		const userDetailsParams: IRequestParams = {
			method: 'DELETE',
			data: { login, email, role },
			url: `${CONSTANTS.baseApiPath}${CONSTANTS.moduleAdminPath}/users`,
		};

		yield call(request, userDetailsParams);

		yield put(deleteUserByAdmin.success('data'));

		if (companyType === 'partner') {
			yield call(getPartnerAdminsSaga, {
				payload: { partnerId: companyId },
			});
		} else {
			yield call(getClientUsersSaga, { payload: companyId });
		}
	} catch (e) {
		logSagaErrorMessage('deleteUserByAdmin', e);
		yield put(deleteUserByAdmin.fail(e));
	}
}

const deleteUserByThemselvesSaga = requestSagaFactory({
	url: `${CONSTANTS.baseApiPath}${CONSTANTS.moduleAdminPath}/users/delete-current-user`,
	actionApi: deleteUserByThemselves,
	logErrorMessage: 'Delete user by themselves',
	method: 'DELETE',
});

function* createUserPasswordSaga(payload: { newPassword: string }) {
	try {
		yield put(createUserPassword.loading());

		const userDetailsParams: IRequestParams = {
			method: 'POST',
			data: payload,
			url: `${CONSTANTS.baseApiPath}${CONSTANTS.moduleAdminPath}/users/???`,
		};

		const { data }: AxiosResponse<ISettingsPayload> = yield call(
			request,
			userDetailsParams,
		);

		yield put(createUserPassword.success(data));
	} catch (e) {
		logSagaErrorMessage('createUserPassword', e);
		yield put(createUserPassword.fail(e));
	}
}

function* updateUserDetailsSaga({
	payload,
}: {
	payload: PersonalInfoModalValues;
}) {
	try {
		yield put(updateUserDetails.loading());

		const userDetailsParams: IRequestParams = {
			method: 'PATCH',
			url: `${CONSTANTS.baseApiPath}${CONSTANTS.moduleAdminPath}/users/current`,
			data: payload,
		};

		yield call(request, userDetailsParams);

		yield put(updateUserDetails.success());
		const { firstName, lastName } = payload;
		yield put(
			updateUserName({
				firstName,
				lastName,
			}),
		);
		yield call(getUserDetailsSaga);
	} catch (e) {
		logSagaErrorMessage('updateUserDetails', e);
		yield put(updateUserDetails.fail(e));
	}
}

function* postUserPhotoSaga({ payload }: { payload: File }) {
	try {
		yield put(postUserPhoto.loading());

		yield call(uploadFileToDB, payload, ENDPOINTS.postUserPhoto, 'logo');

		yield put(postUserPhoto.success());
		yield put(getUserDetails.default());
	} catch (e) {
		logSagaErrorMessage('postUserPhoto', e);
		yield put(postUserPhoto.fail(e));
	}
}

export function* settingsSaga() {
	yield all([
		takeEvery(types.GET_USER_DETAILS.DEFAULT, getUserDetailsSaga),
		// @ts-ignore
		takeEvery(types.UPDATE_USER_DETAILS.DEFAULT, updateUserDetailsSaga),
		// @ts-ignore
		takeEvery(
			types.DELETE_USER_BY_THEMSELVES.DEFAULT,
			deleteUserByThemselvesSaga,
		),
		// @ts-ignore
		takeEvery(types.RESET_PASSWORD_BY_ADMIN.DEFAULT, resetPasswordByAdminSaga),
		// @ts-ignore
		takeEvery(types.DELETE_USER_BY_ADMIN.DEFAULT, deleteUserByAdminSaga),
		// @ts-ignore
		takeEvery(types.CREATE_USER_PASSWORD.DEFAULT, createUserPasswordSaga),
		// @ts-ignore
		takeEvery(types.POST_USER_PHOTO.DEFAULT, postUserPhotoSaga),
		// @ts-ignore
		takeEvery(
			types.GET_LAST_CREATED_SURVEY_LIST.DEFAULT,
			getLastSentSurveyListSaga,
		),
	]);
}
